import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { invokeLimitedFunction } from '@/lib/limited-api';
import { getPinSessionData, isPinSession } from '@/lib/pin-session-utils';

const db = supabase as any;

// Per-company permission toggles (company_policy_settings — see the
// 20260703175000 migration for the policy-name catalog). Missing row =
// disabled = legacy behavior, so consumers can treat `undefined` and
// `false` identically. The read is scoped to the ACTIVE company explicitly —
// RLS here is membership-aware, so it does NOT narrow a multi-membership
// account to one tenant on its own;
// server-side enforcement lives in the RLS branches that call
// has_policy_access() — this hook only drives UI affordances.
export type PolicyName =
  | 'manager_can_edit_schedule'
  | 'manager_can_approve_time_off'
  | 'manager_can_approve_trades'
  | 'manager_sees_team_availability'
  | 'availability_requires_approval'
  | 'corporate_sees_cost_metrics'
  | 'kiosk_require_registered_device';

// Frozen shared fallback. `query.data ?? {}` allocated a NEW object on every
// render while the query was loading, so any consumer putting `policies` in a
// useMemo/useEffect dependency array would churn on each render. One stable
// reference makes it safe to depend on.
const EMPTY_POLICIES: Record<string, boolean> = Object.freeze({});

/** The kiosk company, or null when this isn't a PIN session. */
function readPinCompanyId(): string | null {
  if (!isPinSession()) return null;
  const session = getPinSessionData();
  return session?.profile?.company_id ?? session?.company?.id ?? null;
}

export function useCompanyPolicies() {
  const { profile } = useAuth();

  // A PIN session has no auth profile at all, so `profile?.company_id` is
  // undefined and the query below would stay disabled forever. Read the
  // company off the kiosk session instead — see the PIN branch in queryFn.
  //
  // Held in state and refreshed on `pin_session_updated` rather than read
  // inline on every render, because PermissionsProvider mounts ABOVE
  // BrowserRouter and never unmounts: at first render a kiosk is sitting on
  // the PIN pad with no session at all, and nothing would re-read localStorage
  // when one appears. Same event the other PIN-aware contexts listen on.
  const [pinCompanyId, setPinCompanyId] = useState<string | null>(readPinCompanyId);

  useEffect(() => {
    const sync = () => setPinCompanyId(readPinCompanyId());
    window.addEventListener('pin_session_updated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('pin_session_updated', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // On a PIN session the kiosk identity wins outright rather than merging with
  // whatever email profile may still be in memory: the transport below is
  // chosen by `isPin`, so taking the company from the other identity would
  // cache one company's rows under another's key.
  const isPin = !!pinCompanyId;
  const companyId = (isPin ? pinCompanyId : profile?.company_id) ?? undefined;

  const query = useQuery({
    // The PIN read goes through a different transport and a different identity,
    // so it gets its own cache entry: sharing one key would let a kiosk that
    // had been signed into on the same device serve the web session's cached
    // map, and vice versa. The discriminator goes AFTER companyId so the five
    // existing `invalidateQueries(['company-policies', companyId])` call sites
    // still match by prefix and clear both entries.
    queryKey: ['company-policies', companyId, isPin ? 'pin' : 'jwt'],
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Record<string, boolean>> => {
      // ------------------------------------------------------------
      // Kiosk / PIN
      // ------------------------------------------------------------
      // company_policy_settings is behind RLS and PIN staff hold no JWT, so
      // the direct select below returns nothing for them — not an error, an
      // EMPTY MAP, which resolves every capability to its shipped default.
      // That is why a permission an admin granted on Settings -> Roles &
      // permissions applied on the web and vanished at the kiosk. The
      // limited-company-policies edge function returns the same rows for a
      // validated pin_session.
      if (isPin) {
        const { data, error } = await invokeLimitedFunction('limited-company-policies', {});
        if (error) throw new Error(error.message || 'Failed to load permissions');
        return (data?.policies ?? {}) as Record<string, boolean>;
      }

      const { data, error } = await db
        .from('company_policy_settings')
        // Without a company filter this map is keyed by policy_name ALONE,
        // so for an account with memberships in several tenants another
        // company's row silently wins and flips a policy in this company's
        // view. RLS is membership-aware here, so it cannot do the scoping.
        .select('policy_name, enabled')
        .eq('company_id', companyId);
      if (error) throw error;
      return Object.fromEntries(
        (data ?? []).map((r: { policy_name: string; enabled: boolean }) => [r.policy_name, r.enabled]),
      );
    },
  });

  return {
    policies: query.data ?? EMPTY_POLICIES,
    isLoading: query.isLoading,
    hasPolicy: (name: PolicyName) => query.data?.[name] === true,
  };
}
